// src/pages/PaymentDetails.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Alert,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

function PaymentDetails() {
  const location = useLocation();
  const { scannedData } = location.state || {};

  // We'll store the Pi user object here after successful authentication.
  const [piUser, setPiUser] = useState(null);

  // Payment form states
  const [amount, setAmount] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Extract receiver info from scannedData
  const receiverId = scannedData?.receiverId;      // Must come from QR
  const receiverName = scannedData?.receiverName;  // Optional extra from QR

  // 1) Check if we have a valid receiverId from the QR
  useEffect(() => {
    if (!receiverId) {
      setErrorMsg("No receiver ID found from the scanned QR code. Cannot proceed with payment.");
    }
  }, [receiverId]);

  // 2) Authenticate with Pi SDK and store the user object.
  useEffect(() => {
    if (window.Pi) {
      window.Pi.authenticate(['payments', 'username'])
        .then((authResult) => {
          // authResult.user has the structure { uid: string, username: string }
          setPiUser(authResult.user);
        })
        .catch((err) => {
          console.error("Pi authentication failed:", err);
          setErrorMsg("Pi authentication failed: " + err.message);
        });
    } else {
      setErrorMsg("Pi SDK not loaded. Make sure Pi.init(...) is called in index.html");
    }
  }, []);

  // Commission is 2% of the entered amount
  const commission = amount ? (Number(amount) * 0.02).toFixed(2) : "0.00";
  // Total charge = user's entered amount + 2% commission
  const totalCharge = amount ? (Number(amount) + Number(commission)).toFixed(2) : "0.00";

  // Store or update payment status in your backend
  const storePaymentStatus = async (status, details, paymentId) => {
    try {
      // Pass user_id as the Pi user’s uid, and also pass receiver_id to your backend
      const response = await axios.post("https://lemda.co.uk/api/store_payment.php", {
        payment_id: paymentId,
        user_id: piUser?.uid,
        receiver_id: receiverId,
        status,
        details,
      });
      console.log("Stored payment status:", response.data);
    } catch (err) {
      console.error("Error storing payment status:", err);
    }
  };

  // When user clicks "Pay"
  const handlePayClick = () => {
    // Basic validation checks
    if (!receiverId) {
      setErrorMsg("Cannot pay: No receiver ID from QR.");
      return;
    }
    if (!piUser?.uid) {
      setErrorMsg("Cannot pay: You are not authenticated via Pi SDK.");
      return;
    }
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      setErrorMsg("Please enter a valid amount before proceeding.");
      return;
    }
    setErrorMsg('');
    setConfirmOpen(true);
  };

  // If user confirms the payment in the dialog
  const handleConfirm = () => {
    setConfirmOpen(false);

    // Payment data: user pays totalCharge, memo clarifies details
    const paymentData = {
      amount: totalCharge,
      memo: `Payment to ${receiverName || "receiver"} (ID: ${receiverId}). Commission: ${commission} Pi charged.`,
      metadata: { 
        receiverId, 
        commission, 
        originalAmount: amount 
      },
    };

    // Payment flow callbacks
    const paymentCallbacks = {
      onReadyForServerApproval: (paymentId) => {
        console.log("Payment ready for server approval. Payment ID:", paymentId);
        // Mark status as pending in your backend
        storePaymentStatus("pending", { amount, commission, totalCharge }, paymentId);
      },
      onReadyForServerCompletion: (paymentId, txid) => {
        console.log("Payment complete. Transaction ID:", txid);
        setPaymentStatus("Payment Completed!");
        // Mark status as completed
        storePaymentStatus("completed", { txid, amount, commission, totalCharge }, paymentId);
      },
      onCancel: (paymentId) => {
        console.log("Payment canceled. Payment ID:", paymentId);
        setPaymentStatus("Payment Canceled");
        // Mark status as cancelled
        storePaymentStatus("cancelled", { amount, commission, totalCharge }, paymentId);
      },
      onError: (error, payment) => {
        console.error("Payment error:", error, payment);
        setPaymentStatus("Payment Error Occurred");
        // Mark status as error
        storePaymentStatus("error", { error: error.toString(), payment }, payment?.paymentId || "unknown");
      },
    };

    // Re-authenticate to ensure we have "payments" scope (a best practice).
    if (window.Pi) {
      window.Pi.authenticate(['payments'])
        .then(() => {
          if (typeof window.Pi.createPayment === 'function') {
            window.Pi.createPayment(paymentData, paymentCallbacks);
            console.log("Payment initiation triggered with data:", paymentData);
          } else {
            const errMsg = "Pi SDK Payment function is not available";
            console.error(errMsg);
            setErrorMsg(errMsg);
          }
        })
        .catch((error) => {
          console.error("Authentication failed (payments):", error);
          setErrorMsg("Authentication (payments) failed: " + error.message);
        });
    } else {
      const errMsg = "Pi SDK not loaded";
      console.error(errMsg);
      setErrorMsg(errMsg);
    }
  };

  return (
    <Box p={2} textAlign="center">
      <Paper elevation={4} sx={{ p: 3, maxWidth: 500, margin: '0 auto' }}>
        <Typography variant="h5" gutterBottom>
          Payment Details
        </Typography>

        {/* Show the receiver info if present */}
        {receiverId && (
          <>
            <Typography variant="body1">
              <strong>Receiver Name:</strong> {receiverName || "Unknown"}
            </Typography>
            <Typography variant="body1">
              <strong>Receiver ID:</strong> {receiverId}
            </Typography>
          </>
        )}

        <Box mt={3}>
          <TextField
            label="Enter Amount (Pi)"
            type="number"
            variant="outlined"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Box>
        {amount && (
          <Box mt={2}>
            <Typography variant="body2">
              Commission (2%): {commission} Pi
            </Typography>
            <Typography variant="body2">
              Total Charge (Amount + Commission): {totalCharge} Pi
            </Typography>
          </Box>
        )}
        <Box mt={3}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handlePayClick}
            disabled={!receiverId || !piUser?.uid}
          >
            Pay
          </Button>
        </Box>

        {/* Payment status message */}
        {paymentStatus && (
          <Box mt={2}>
            <Typography variant="h6" color="secondary">
              {paymentStatus}
            </Typography>
          </Box>
        )}

        {/* Error message */}
        {errorMsg && (
          <Box mt={2}>
            <Alert severity="error">{errorMsg}</Alert>
          </Box>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to pay <strong>{amount} Pi</strong> to {receiverName || "receiver"}.<br />
            Commission (2%): <strong>{commission} Pi</strong><br />
            Total Charge: <strong>{totalCharge} Pi</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PaymentDetails;
